import { useMemo } from 'react';
import type React from 'react';

import { Modal, ModalBody, Text } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core-react/i18n';

import { RoundShareIconButton, ShareChannelButton } from '#src/app/shared/components/share';
import { useRestoreFocusOnClose } from '#src/app/shared/hooks/focus';
import type { ShareAction } from '#src/app/shared/hooks/share';

import {
	ACTION_BUTTON_TRACKING_DATA,
	CHANNEL_GRID_TRACKING_DATA,
	CHANNEL_LABELS,
	COPY_LINK_LABEL,
	COPY_LINK_SUCCESS_LABEL,
	SHARE_ICON_SIZE_CLASSNAME,
	SHEET_CHANNELS,
	type SheetChannel,
} from './constants';
import { useCopyLinkAnnouncement } from './hooks/useCopyLinkAnnouncement';

type Props = {
	isOpen: boolean;
	/** Single dismissal path — the sheet has no close button, so this covers ESC and outside-click. */
	onOpenChange: (isOpen: boolean) => void;
	shareActions: readonly ShareAction[];
	/**
	 * Element to hand focus back to when the sheet is dismissed — point this at whatever should read
	 * next (typically the heading of the content behind the sheet). The sheet opens programmatically,
	 * so react-aria has no trigger to restore to and focus would otherwise fall to `<body>`.
	 */
	restoreFocusRef: React.RefObject<HTMLElement | null>;
};

export const ContactDecisionMakerShareSheet: React.FC<Props> = ({
	isOpen,
	onOpenChange,
	shareActions,
	restoreFocusRef,
}) => {
	const { translate } = useI18n();
	const { isAnnouncing, announce } = useCopyLinkAnnouncement();

	// `SHEET_CHANNELS` is both the allow-list and the spec's display order, so walking it and keeping
	// the channels the query actually returned is the whole filter. Device availability is handled
	// upstream — the middle-end drops channels the user agent can't use (e.g. sms off desktop), and
	// useShareActions drops the native-share-gated ones — so anything still here is usable.
	const channelActions = useMemo(() => {
		const actionByChannel = new Map(shareActions.map((action) => [action.channel, action]));
		return SHEET_CHANNELS.map((channel) => {
			const action = actionByChannel.get(channel);
			return action ? { channel, action } : undefined;
		}).filter((entry): entry is { channel: SheetChannel; action: ShareAction } => !!entry);
	}, [shareActions]);

	const copyAction = useMemo(() => shareActions.find(({ channel }) => channel === 'copy'), [shareActions]);
	// Nothing to offer when the links query failed — the caller keeps the sheet closed while it loads.
	const hasNothingToShare = channelActions.length === 0 && !copyAction;

	useRestoreFocusOnClose(isOpen, restoreFocusRef);

	const heading = translate('corgi.contact_decision_maker.share_sheet.heading');

	return (
		// No `onClose` on purpose: the DS renders its close icon only when `onClose` is set, and this
		// sheet is specced with a grabber and no X. ESC and outside-click still dismiss, both through
		// `onOpenChange`, which keeps a single dismissal path. (If an X is ever added, it must be an
		// `onClose={noop}` + `<ModalHeader>` as a DIRECT child of `<Modal>` — see PetitionShareSlideUp.)
		// No `transformToSheet`: the DS already gives a bottom sheet at `sm` widths and a centered dialog
		// above them. Forcing the sheet everywhere pinned it to the bottom-left corner on desktop.
		<Modal isOpen={isOpen} onOpenChange={onOpenChange} aria-label={heading} className="z-100">
			<ModalBody className="pb-6">
				<div className="flex flex-col">
					{/* Grabber is a sheet affordance. The DS drives the sheet with `max-md:`, so at `md` and up
					    this is a centred dialog and the grabber would suggest a drag that does nothing. */}
					<div
						aria-hidden
						className="mx-auto h-[5px] w-[38px] rounded-full bg-[var(--color-stroke-weaker)] md:hidden"
					/>

					<Text as="h2" className="text-size-heading-xs text-typography-strong mt-2.5">
						{heading}
					</Text>
					<Text className="text-size-body-small text-typography-weak mt-1">
						{translate('corgi.contact_decision_maker.share_sheet.body')}
					</Text>

					{/* Centered flex, not a fixed 4-column grid: the sheet declares four channels but the
					    middle-end drops the ones the device can't use (SMS off desktop), and a rigid grid
					    packs the survivors into the leading columns instead of centering them. */}
					<div
						className="mt-4 flex flex-wrap justify-center gap-x-8 gap-y-5"
						data-testid="contact-decision-maker-share-sheet-channels"
					>
						{channelActions.map(({ channel, action }) => (
							<RoundShareIconButton
								key={channel}
								className={SHARE_ICON_SIZE_CLASSNAME}
								buttonConfig={{ ShareAction: action.ShareAction, channel, label: CHANNEL_LABELS[channel] }}
								trackingData={CHANNEL_GRID_TRACKING_DATA}
							/>
						))}
					</div>

					{/* Single-column grid so the button stretches full width — same trick as the PSF slide-up,
					    since ShareChannelButton renders an inline-flex DS Button with no width control. */}
					{copyAction && (
						<div className="mt-4 grid grid-cols-1" data-testid="contact-decision-maker-share-sheet-copy-link">
							<ShareChannelButton
								buttonConfig={{
									ShareAction: copyAction.ShareAction,
									channel: 'copy',
									label: COPY_LINK_LABEL,
									successLabel: COPY_LINK_SUCCESS_LABEL,
								}}
								variant="secondary"
								trackingData={ACTION_BUTTON_TRACKING_DATA}
								// Announced after the clipboard write resolves, and only when it succeeded — announcing
								// on the way in reports a failed copy as a success.
								onAfterShare={(success) => {
									if (success) announce();
								}}
							/>
						</div>
					)}

					{hasNothingToShare && (
						<Text
							role="alert"
							className="text-size-body-small text-typography-weak mt-4 text-center"
							data-testid="contact-decision-maker-share-sheet-unavailable"
						>
							{translate('corgi.contact_decision_maker.share_sheet.unavailable')}
						</Text>
					)}

					{/* `output` rather than a div with `role="status"`: it carries that role implicitly and
					    jsx-a11y/prefer-tag-over-role rejects the explicit form. `aria-live` stays explicit
					    because not every screen reader applies the role's implicit politeness. */}
					<output aria-live="polite" className="sr-only">
						{isAnnouncing ? translate('corgi.contact_decision_maker.share_sheet.copy_confirmation') : ''}
					</output>
				</div>
			</ModalBody>
		</Modal>
	);
};
